import { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Lightbulb, X } from 'lucide-react';
import { Button } from '../common/Button';
import { InlineCodeText } from '../common/InlineCodeText';

interface HintsPanelProps {
  hints: [string, string, string];
  isOpen: boolean;
  onClose: () => void;
  onHintRequest: (hintNumber: number) => void;
}

export function HintsPanel({ hints, isOpen, onClose, onHintRequest }: HintsPanelProps) {
  const [revealedHints, setRevealedHints] = useState<number[]>([]);
  const panelRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (!isOpen) return;

    closeButtonRef.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && event.target instanceof Node && panelRef.current?.contains(event.target)) {
        // Handle panel focus before jsCoq's document-level shortcuts.
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', closeOnEscape, true);
    return () => window.removeEventListener('keydown', closeOnEscape, true);
  }, [isOpen, onClose]);

  const revealHint = (hintNumber: number) => {
    if (!revealedHints.includes(hintNumber)) {
      setRevealedHints([...revealedHints, hintNumber]);
      onHintRequest(hintNumber);
    }
  };

  return (
    <motion.section
      ref={panelRef}
      id="level-hints"
      aria-labelledby="level-hints-title"
      initial={false}
      animate={{ opacity: isOpen ? 1 : 0, x: isOpen ? 0 : -16 }}
      transition={{ duration: reduceMotion ? 0 : 0.18 }}
      className="absolute inset-0 flex min-h-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800"
    >
      <div className="mb-4 flex shrink-0 items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Lightbulb className="text-warning-500 dark:text-warning-400" size={20} aria-hidden="true" />
          <h3 id="level-hints-title" className="font-semibold text-gray-900 dark:text-gray-100">Hints</h3>
        </div>
        <button
          ref={closeButtonRef}
          type="button"
          aria-label="Close hints"
          onClick={onClose}
          className="rounded p-1 text-gray-600 hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500 dark:text-gray-300 dark:hover:bg-gray-700"
        >
          <X size={18} aria-hidden="true" />
        </button>
      </div>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain">
        {hints.map((hint, idx) => {
          const hintNumber = idx + 1;
          const isRevealed = revealedHints.includes(hintNumber);

          return (
            <div key={hintNumber} className="rounded border border-gray-200 p-3 dark:border-gray-600">
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Hint {hintNumber}</span>
                {!isRevealed && (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    aria-label={`Reveal hint ${hintNumber}`}
                    onClick={() => revealHint(hintNumber)}
                  >
                    Reveal
                  </Button>
                )}
              </div>
              {isRevealed && <p className="whitespace-pre-line break-words text-sm text-gray-600 dark:text-gray-300"><InlineCodeText>{hint}</InlineCodeText></p>}
            </div>
          );
        })}
      </div>
    </motion.section>
  );
}
